import {Component, OnInit, OnDestroy, forwardRef, Output, EventEmitter} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ContactsService } from 'src/app/business-service/contacts/contacts.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-contacts-selection',
  templateUrl: './contacts-selection.component.html',
  styleUrls: ['./contacts-selection.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactsSelectionComponent),
      multi: true
    }
  ]
})
export class ContactsSelectionComponent implements OnInit, OnDestroy, ControlValueAccessor {

  // Pagination and data (kept as-is)
  paginationSettings: any = {
    pageNumber: 1,
    pageSize: 10
  };
  visiblePages: number[] = [];
  noOfRows: any = 0;

  allData: any;
  data: any = [];
  hidePaginations: boolean = false;

  // Search & filters
  searchKeyword: string = '';
  filters: any = {};

  // Selection state: map of contactId -> contact object (or true)
  private selectedMap = new Map<string | number, any>();

  // Reactive search handling
  private searchSubject = new Subject<string>();
  private searchSub!: Subscription;

  // ControlValueAccessor
  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };
  isDisabled = false;

  @Output() selectionChange = new EventEmitter<(string | number)[]>();

  // Add this property near the top of the class
  isAllContactsSelected = false;

  constructor(private _contactService: ContactsService) { }

  async ngOnInit() {
    // subscribe to debounced search
    this.searchSub = this.searchSubject.pipe(debounceTime(500)).subscribe(() => {
      // when searching, reset to first page
      this.paginationSettings.pageNumber = 1;
      this.loadPageData();
    });

    await this.getAllContacts();
  }

  ngOnDestroy() {
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
  }

  /* -------------------------
     API / Pagination methods
     ------------------------- */

  async getAllContacts(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._contactService
        .getAllContacts(
          this.paginationSettings.pageNumber,
          this.paginationSettings.pageSize,
          null,
          this.searchKeyword,
          this.filters
        )
        .subscribe(
          (res: any) => {
            this.allData = res;
            this.data = this.allData.data;
            // preserve paginationSettings as the API returns (same behavior you had)
            this.paginationSettings = this.allData;

            if (this.data && this.data.length) {
              // remove data from pagination settings (original behavior)
              delete this.paginationSettings.data;
              this.noOfRows = this.data.length;
              this.updateVisiblePages();
            }

            this.hidePaginations = !!(this.data && this.data.length === 0);

            // no change to selectedMap here — selection persists across pages
            resolve();
          },
          (error: any) => {
            reject(error);
          }
        );
    });
  }

  updateVisiblePages() {
    const currentPage = this.paginationSettings.pageNumber;
    const totalPages = this.paginationSettings.totalPages;

    let startPage: number, endPage: number;
    if (totalPages <= 5) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= 3) {
        startPage = 1;
        endPage = 5;
      } else if (currentPage + 2 >= totalPages) {
        startPage = totalPages - 4;
        endPage = totalPages;
      } else {
        startPage = currentPage - 2;
        endPage = currentPage + 2;
      }
    }

    this.visiblePages = Array.from(Array(endPage + 1 - startPage).keys()).map(i => startPage + i);
  }

  onPageChange(page: number) {
    if (page !== this.paginationSettings.pageNumber) {
      this.paginationSettings.pageNumber = page;
      this.updateVisiblePages();
      this.loadPageData();
    }
  }

  onNext() {
    if (this.paginationSettings.pageNumber < this.paginationSettings.totalPages) {
      this.paginationSettings.pageNumber++;
      this.updateVisiblePages();
      this.loadPageData();
    }
  }

  onPrevious() {
    if (this.paginationSettings.pageNumber > 1) {
      this.paginationSettings.pageNumber--;
      this.updateVisiblePages();
      this.loadPageData();
    }
  }

  onPageSizeChange() {
    this.paginationSettings.pageNumber = 1;
    this.updateVisiblePages();
    this.loadPageData();
  }

  async loadPageData() {
    await this.getAllContacts();
  }

  /* -------------------------
     Search handling
     ------------------------- */

  onSearchInput(event: any) {
    // update bound variable and push to debounced subject
    const value = event.target.value;
    this.searchKeyword = value;
    this.searchSubject.next(value);
  }

  /* -------------------------
     Selection handling
     ------------------------- */

  // check if a single contact is selected
  isSelected(contactId: string | number): boolean {
    return this.selectedMap.has(contactId);
  }

  // toggle selection for a single contact
  onItemToggle(item: any, event: any) {
    const id = item.contactId;
    const checked = event.target.checked;
    if (checked) {
      this.selectedMap.set(id, item);
    } else {
      this.selectedMap.delete(id);
    }
    this.emitSelectionChange();
    this.onTouched();
  }

  // select/unselect all contacts on current page
  toggleSelectAll(event: any) {
    const checked = event.target.checked;
    if (!this.data || !this.data.length) {
      return;
    }

    for (const item of this.data) {
      const id = item.contactId;
      if (checked) {
        this.selectedMap.set(id, item);
      } else {
        this.selectedMap.delete(id);
      }
    }

    this.emitSelectionChange();
    this.onTouched();
  }

  // Add this new method to handle selecting all contacts
  async toggleSelectAllContacts() {
    if (this.isAllContactsSelected) {
      // Unselect all contacts
      this.selectedMap.clear();
      this.isAllContactsSelected = false;
    } else {
      // Select all contacts by loading all contacts without pagination
      const allContacts = await this._contactService.getAllContacts(-1, -1, null, this.searchKeyword, this.filters).toPromise();

      if (allContacts && allContacts.data) {
        // Clear existing selection and add all contacts
        this.selectedMap.clear();
        allContacts.data.forEach((contact: any) => {
          this.selectedMap.set(contact.contactId, contact);
        });
        this.isAllContactsSelected = true;
      }
    }

    this.emitSelectionChange();
    this.onTouched();
  }

  // helpers for header checkbox states
  isAllSelected(): boolean {
    if (this.isAllContactsSelected) {
      return true;
    }
    if (!this.data || this.data.length === 0) {
      return false;
    }
    return this.data.every((d: any) => this.selectedMap.has(d.contactId));
  }

  isIndeterminate(): boolean {
    if (!this.data || this.data.length === 0) {
      return false;
    }
    const someSelected = this.data.some((d: any) => this.selectedMap.has(d.contactId));
    return someSelected && !this.isAllSelected();
  }

  // expose selected ids for view and for parent
  get selectedIds(): (string | number)[] {
    return Array.from(this.selectedMap.keys());
  }

  get selectedContacts(): any[] {
    return Array.from(this.selectedMap.values());
  }
  // emit changes both to form and output
  private emitSelectionChange() {
    this.onChange(this.selectedContacts);
    this.selectionChange.emit(this.selectedContacts);
  }

  // optional trackBy for ngFor
  trackByContactId(index: number, item: any) {
    return item ? item.contactId : index;
  }

  /* -------------------------
     ControlValueAccessor methods
     ------------------------- */

  writeValue(obj: any): void {
    this.selectedMap.clear();
    if (Array.isArray(obj) && obj.length > 0) {
      for (const id of obj) {
        this.selectedMap.set(id, true);
      }
    }
    this.selectionChange.emit(this.selectedIds);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn || (() => { });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn || (() => { });
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = !!isDisabled;
  }
}
